import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { getBYLocations } from "../../repositories/BilletsYardRepository";

interface LocationOption {
  Value: number;
  Title: string;
}

const ALL_OPTION: LocationOption = { Value: 0, Title: "All" };

type Props = {
  value: number | null;
  onChange: (value: number | null, name?: string) => void;
};

export const LocationFilter = ({ value, onChange }: Props) => {
  const [locations, setLocations] = useState<LocationOption[]>([ALL_OPTION]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await getBYLocations();
      if (resp.ok) {
        const rows = get(resp, "data.tables[0].rows", []) as LocationOption[];
        setLocations([ALL_OPTION, ...rows.filter((l) => l.Value !== 0)]);
      } else {
        setError(resp.message);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while loading locations",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const selected =
    locations.find((l) => l.Value === (value ?? 0)) ?? ALL_OPTION;

  return (
    <>
      <Autocomplete
        options={locations}
        getOptionLabel={(option) => option.Title}
        isOptionEqualToValue={(option, val) => option.Value === val.Value}
        value={selected}
        disableClearable
        loading={isLoading}
        onChange={(_event, newValue) => {
          const next = newValue ?? ALL_OPTION;
          onChange(next.Value === 0 ? null : next.Value, next.Title);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Location"
            variant="outlined"
            fullWidth
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error loading Locations"
      />
    </>
  );
};
